
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useSignatures } from "@/features/messages/hooks/useSignatures";
import { Signature } from "@/features/messages/types/signatures";
import { Edit2, Trash2, PlusCircle, Mail, LogOut, AlertCircle, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Alert, AlertTitle, AlertDescription as ShadcnAlertDescription } from "@/components/ui/alert";

interface ConnectedAccount {
  id: string;
  email_address: string;
  provider: 'gmail' | 'outlook';
  last_sync_at?: string | null; // Added for displaying sync status
}

export const EmailSettings = () => {
  const [emailSync, setEmailSync] = useState(false);
  const { signatures, addSignature, updateSignature, removeSignature } = useSignatures();
  
  const [newSignatureName, setNewSignatureName] = useState('');
  const [newSignatureContent, setNewSignatureContent] = useState('');
  
  const [editingSignature, setEditingSignature] = useState<Signature | null>(null);
  const [editSignatureName, setEditSignatureName] = useState('');
  const [editSignatureContent, setEditSignatureContent] = useState('');

  const [isConnectingGmail, setIsConnectingGmail] = useState(false);
  const [gmailConnectionError, setGmailConnectionError] = useState<string | null>(null);
  const [connectedAccounts, setConnectedAccounts] = useState<ConnectedAccount[]>([]);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(true);
  const [syncingAccountId, setSyncingAccountId] = useState<string | null>(null); // Track which account is syncing

  const location = useLocation();
  const navigate = useNavigate();

  const fetchConnectedAccounts = async () => {
    setIsLoadingAccounts(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setIsLoadingAccounts(false);
      setConnectedAccounts([]);
      return;
    }

    // Fetch last_sync_at from email_accounts table
    const { data, error } = await supabase
      .from('email_accounts')
      .select('id, email_address, provider, last_sync_at')
      .eq('user_id', user.id);

    if (error) {
      toast.error("Failed to fetch connected email accounts.", { description: error.message });
      setConnectedAccounts([]);
    } else {
      setConnectedAccounts(data as ConnectedAccount[] || []);
    }
    setIsLoadingAccounts(false);
  };

  useEffect(() => {
    fetchConnectedAccounts();
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const code = searchParams.get("code");
    // const state = searchParams.get("state"); // TODO: Validate state

    if (code) {
      setIsConnectingGmail(true);
      setGmailConnectionError(null);
      console.log("OAuth code found in URL:", code);

      // Remove code from URL to prevent re-processing
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.delete("code");
      newSearchParams.delete("state");
      navigate(`${location.pathname}?${newSearchParams.toString()}`, { replace: true });
      
      supabase.functions.invoke('gmail-oauth-flow', {
        body: { code: code },
      }).then(({ data, error }) => {
        setIsConnectingGmail(false);
        if (error) {
          console.error("Gmail connection error (token exchange):", error);
          setGmailConnectionError(error.message || "Failed to connect Gmail account.");
          toast.error("Gmail Connection Failed", { description: error.message });
        } else if (data && data.success) {
          console.log("Gmail connected successfully:", data);
          toast.success(data.message || "Gmail account connected successfully!", { description: `Email: ${data.email}`});
          fetchConnectedAccounts(); // Refresh connected accounts list
        } else {
          // Handle cases where data might not have success property or custom error structure from function
          const errMsg = data?.error || data?.details || "An unknown error occurred during Gmail connection.";
          console.error("Gmail connection failed (response data):", data);
          setGmailConnectionError(errMsg);
          toast.error("Gmail Connection Failed", { description: errMsg });
        }
      }).catch(invokeError => {
        setIsConnectingGmail(false);
        console.error("Gmail connection error (invoke):", invokeError);
        setGmailConnectionError(invokeError.message || "An unexpected error occurred.");
        toast.error("Gmail Connection Failed", { description: invokeError.message });
      });
    }
  }, [location, navigate]);

  const handleConnectGmail = async () => {
    setIsConnectingGmail(true);
    setGmailConnectionError(null);
    try {
      const { data, error } = await supabase.functions.invoke('gmail-oauth-flow', {
        body: { authorize: true },
      });

      if (error) throw error;

      if (data.authUrl) {
        window.location.href = data.authUrl;
      } else {
        throw new Error("Authorization URL not received from server.");
      }
    } catch (error: any) {
      console.error("Error initiating Gmail connection:", error);
      setGmailConnectionError(error.message || "Failed to initiate Gmail connection.");
      toast.error("Connection Error", { description: error.message });
      setIsConnectingGmail(false);
    }
  };

  const handleDisconnectGmail = async (accountId: string) => {
    const account = connectedAccounts.find(acc => acc.id === accountId);
    if (!account) return;

    const confirmed = confirm(`Are you sure you want to disconnect ${account.email_address}?`);
    if (!confirmed) return;

    try {
      const { error } = await supabase
        .from('email_accounts')
        .delete()
        .eq('id', accountId);

      if (error) throw error;

      toast.success(`Successfully disconnected ${account.email_address}.`);
      fetchConnectedAccounts(); // Refresh the list
    } catch (error: any) {
      console.error("Error disconnecting Gmail account:", error);
      toast.error("Disconnection Failed", { description: error.message });
    }
  };

  const handleSyncEmails = async (accountId: string) => {
    setSyncingAccountId(accountId);
    toast.info("Email synchronization started...");
    try {
      const { data, error } = await supabase.functions.invoke('email-sync-agent', {
        body: { emailAccountId: accountId },
      });

      if (error) {
        console.error("Error invoking email-sync-agent:", error);
        toast.error("Email Sync Failed", { description: error.message });
      } else if (data && data.success) {
        toast.success(data.message || "Emails synced successfully.", { description: `${data.emailsSynced} email(s) processed.` });
        fetchConnectedAccounts(); // Refresh to show updated last_sync_at
      } else {
        const errMsg = data?.error || data?.details || "An unknown error occurred during email sync.";
        console.error("Email sync failed (response data):", data);
        toast.error("Email Sync Failed", { description: errMsg });
      }
    } catch (invokeError: any) {
      console.error("Error invoking email-sync-agent (catch):", invokeError);
      toast.error("Email Sync Invocation Failed", { description: invokeError.message });
    } finally {
      setSyncingAccountId(null);
    }
  };

  const handleAddSignature = () => {
    if (newSignatureName.trim() && newSignatureContent.trim()) {
      addSignature(newSignatureName, newSignatureContent);
      setNewSignatureName('');
      setNewSignatureContent('');
    }
  };

  const handleStartEdit = (signature: Signature) => {
    setEditingSignature(signature);
    setEditSignatureName(signature.name);
    setEditSignatureContent(signature.content);
  };

  const handleSaveEdit = () => {
    if (editingSignature && editSignatureName.trim() && editSignatureContent.trim()) {
      updateSignature(editingSignature.id, editSignatureName, editSignatureContent);
      setEditingSignature(null);
    }
  };


  return (
    <div className="space-y-8">
      <Card>
        <CardHeader>
          <CardTitle>General Email Settings</CardTitle>
          <CardDescription>
            Configure overall email integration and synchronization.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="email-sync-switch">Email Synchronization</Label>
              <p className="text-sm text-muted-foreground">
                Enable global email sending and receiving capabilities.
              </p>
            </div>
            <Switch
              id="email-sync-switch"
              checked={emailSync}
              onCheckedChange={setEmailSync}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Connected Email Accounts</CardTitle>
          <CardDescription>
            Manage your connected Gmail and other email accounts.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {gmailConnectionError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Gmail Connection Error</AlertTitle>
              <ShadcnAlertDescription>{gmailConnectionError}</ShadcnAlertDescription>
            </Alert>
          )}

          <div className="space-y-4 p-4 border rounded-md">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-red-500" />
                <h3 className="text-md font-semibold">Gmail</h3>
              </div>
              <Button 
                onClick={handleConnectGmail} 
                disabled={isConnectingGmail}
                variant="outline"
              >
                {isConnectingGmail ? "Connecting..." : "Connect Gmail Account"}
              </Button>
            </div>
            {isLoadingAccounts && <p className="text-sm text-muted-foreground">Loading connected accounts...</p>}
            {!isLoadingAccounts && connectedAccounts.filter(acc => acc.provider === 'gmail').length === 0 && (
              <p className="text-sm text-muted-foreground">No Gmail accounts connected.</p>
            )}
            {!isLoadingAccounts && connectedAccounts.filter(acc => acc.provider === 'gmail').map(account => (
              <div key={account.id} className="flex items-center justify-between p-3 border-t">
                <div>
                  <p className="text-sm font-medium">{account.email_address}</p>
                  <p className="text-xs text-muted-foreground">
                    Last synced: {account.last_sync_at ? new Date(account.last_sync_at).toLocaleString() : 'Never'}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSyncEmails(account.id)}
                    disabled={syncingAccountId === account.id}
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${syncingAccountId === account.id ? 'animate-spin' : ''}`} />
                    {syncingAccountId === account.id ? "Syncing..." : "Sync Now"}
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleDisconnectGmail(account.id)}
                    className="text-destructive hover:text-destructive-foreground hover:bg-destructive/90"
                    disabled={syncingAccountId === account.id}
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Disconnect
                  </Button>
                </div>
              </div>
            ))}
          </div>
          {/* Placeholder for Outlook and other providers */}
          {/* 
          <div className="space-y-2 p-4 border rounded-md bg-gray-50 dark:bg-gray-800/30 opacity-50">
            <h3 className="text-md font-semibold">Outlook / Microsoft 365</h3>
            <p className="text-sm text-muted-foreground">Outlook integration coming soon.</p>
            <Button disabled variant="outline">Connect Outlook Account</Button>
          </div>
          */}
        </CardContent>
        <CardFooter>
            <p className="text-xs text-muted-foreground">
                Note: Ensure your GMAIL_CLIENT_ID, GMAIL_CLIENT_SECRET, and GMAIL_REDIRECT_URI_BASE environment variables are correctly set up in your Supabase project for Gmail integration to work.
                The redirect URI must be authorized in your Google Cloud Console.
            </p>
        </CardFooter>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Manage Signatures</CardTitle>
          <CardDescription>
            Create, edit, or delete your email signatures.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4 pt-0"> {/* Adjusted pt-6 to pt-0 if the above block is removed or kept */}
            
            {editingSignature ? (
              <div className="space-y-3 p-4 border rounded-md">
                <h3 className="text-md font-semibold">Edit Signature</h3>
                <div>
                  <Label htmlFor="editSignatureName">Signature Name</Label>
                  <Input 
                    id="editSignatureName"
                    value={editSignatureName}
                    onChange={(e) => setEditSignatureName(e.target.value)}
                    placeholder="e.g., Professional Closing"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="editSignatureContent">Signature Content</Label>
                  <Textarea 
                    id="editSignatureContent"
                    value={editSignatureContent}
                    onChange={(e) => setEditSignatureContent(e.target.value)}
                    placeholder="Your signature text..."
                    className="min-h-[80px] mt-1"
                  />
                </div>
                <div className="flex space-x-2">
                  <Button onClick={handleSaveEdit} size="sm">Save Changes</Button>
                  <Button variant="outline" onClick={() => setEditingSignature(null)} size="sm">Cancel</Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3 p-4 border rounded-md bg-gray-50 dark:bg-gray-800/30">
                <h3 className="text-md font-semibold">Add New Signature</h3>
                <div>
                  <Label htmlFor="newSignatureName">Signature Name</Label>
                  <Input 
                    id="newSignatureName"
                    value={newSignatureName}
                    onChange={(e) => setNewSignatureName(e.target.value)}
                    placeholder="e.g., Casual Closing"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="newSignatureContent">Signature Content</Label>
                  <Textarea 
                    id="newSignatureContent"
                    value={newSignatureContent}
                    onChange={(e) => setNewSignatureContent(e.target.value)}
                    placeholder="Best regards, [Your Name]"
                    className="min-h-[80px] mt-1"
                  />
                </div>
                <Button onClick={handleAddSignature} size="sm">
                  <PlusCircle className="h-4 w-4 mr-2" />
                  Add Signature
                </Button>
              </div>
            )}

            {signatures.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-md font-semibold mt-4">Saved Signatures</h3>
                <ul className="space-y-2">
                  {signatures.map(sig => (
                    <li key={sig.id} className="p-3 border rounded-md flex justify-between items-start">
                      <div>
                        <p className="font-medium">{sig.name}</p>
                        <pre className="text-xs text-muted-foreground whitespace-pre-wrap font-sans">{sig.content}</pre>
                      </div>
                      <div className="flex space-x-1.5 flex-shrink-0 ml-2">
                        <Button variant="ghost" size="icon" onClick={() => handleStartEdit(sig)} aria-label="Edit signature">
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => removeSignature(sig.id)} className="text-destructive hover:text-destructive" aria-label="Delete signature">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {signatures.length === 0 && !editingSignature && (
               <p className="text-sm text-muted-foreground text-center py-4">No signatures saved yet.</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
